import { useState, useRef, useMemo } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getProject,
  getRecordings,
  uploadRecording,
  deleteRecording,
  startTranscription,
  getTeamsMeetings,
  getTeamsTranscripts,
  importTeamsTranscript,
  getTranscript,
  downloadTranscript,
  Recording,
  TeamsMeeting,
  TeamsTranscript,
} from '../services/api'
import { getClient } from '../lib/aperture'

const glassPanel = {
  background: 'rgba(15, 23, 42, 0.6)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(100, 180, 255, 0.1)',
}

export default function Recordings() {
  const { projectId } = useParams<{ projectId: string }>()
  const queryClient = useQueryClient()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [uploading, setUploading] = useState(false)
  const [statusFilter, setStatusFilter] = useState('all')
  const [sortBy, setSortBy] = useState('date')

  // Teams import state
  const [showTeamsModal, setShowTeamsModal] = useState(false)
  const [selectedMeeting, setSelectedMeeting] = useState<TeamsMeeting | null>(null)
  const [meetingTranscripts, setMeetingTranscripts] = useState<TeamsTranscript[]>([])
  const [loadingTranscripts, setLoadingTranscripts] = useState(false)

  // Teams filter state
  const [teamsDateRange, setTeamsDateRange] = useState<'30d' | '60d' | '90d'>('30d')
  const [teamsSearch, setTeamsSearch] = useState('')
  const [transcriptAvailability, setTranscriptAvailability] = useState<Record<string, boolean | 'loading' | 'unknown'>>({})
  const [showOnlyWithTranscripts, setShowOnlyWithTranscripts] = useState(false)

  // Transcript view state
  const [showTranscriptModal, setShowTranscriptModal] = useState(false)
  const [viewingTranscript, setViewingTranscript] = useState<{ sections: Array<{ start_time: number; speaker?: string; text: string }> } | null>(null)
  const [transcriptRecording, setTranscriptRecording] = useState<Recording | null>(null)

  // Delete confirmation state
  const [deleteConfirmRecording, setDeleteConfirmRecording] = useState<Recording | null>(null)

  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => getProject(projectId!).then((res) => res.data),
    enabled: !!projectId,
  })

  const { data: recordings, isLoading } = useQuery({
    queryKey: ['recordings', projectId],
    queryFn: () => getRecordings(projectId).then((res) => res.data),
    enabled: !!projectId,
    refetchInterval: 5000,
  })

  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => uploadRecording(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recordings', projectId] })
      setUploading(false)
    },
    onError: () => {
      setUploading(false)
    },
  })

  const transcribeMutation = useMutation({
    mutationFn: startTranscription,
    onSuccess: () => {
      getClient()?.capture('output_metric', { metric_key: 'transcriptions_started', value: 1 })
      queryClient.invalidateQueries({ queryKey: ['recordings', projectId] })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: deleteRecording,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recordings', projectId] })
    },
  })

  // Calculate date range based on selection
  const getTeamsDateRange = () => {
    const endDate = new Date()
    const startDate = new Date()
    const days = teamsDateRange === '30d' ? 30 : teamsDateRange === '60d' ? 60 : 90
    startDate.setDate(startDate.getDate() - days)
    return { startDate, endDate }
  }

  // Teams meetings query - uses selected date range
  const { data: teamsMeetings, isLoading: loadingMeetings } = useQuery({
    queryKey: ['teamsMeetings', teamsDateRange],
    queryFn: () => {
      const { startDate, endDate } = getTeamsDateRange()
      return getTeamsMeetings({
        start_date: startDate.toISOString(),
        end_date: endDate.toISOString(),
        limit: 100
      }).then((res) => res.data)
    },
    enabled: showTeamsModal,
  })

  // Filter meetings by search and transcript availability
  const filteredTeamsMeetings = useMemo(() => {
    if (!teamsMeetings) return []
    let filtered = [...teamsMeetings]

    // Filter by search query
    if (teamsSearch) {
      const searchLower = teamsSearch.toLowerCase()
      filtered = filtered.filter((m) =>
        m.subject.toLowerCase().includes(searchLower) ||
        m.organizer?.toLowerCase().includes(searchLower)
      )
    }

    // Filter by transcript availability
    if (showOnlyWithTranscripts) {
      filtered = filtered.filter((m) => transcriptAvailability[m.id] === true)
    }

    return filtered
  }, [teamsMeetings, teamsSearch, showOnlyWithTranscripts, transcriptAvailability])

  // Teams import mutation
  const importMutation = useMutation({
    mutationFn: importTeamsTranscript,
    onSuccess: () => {
      getClient()?.capture('output_metric', { metric_key: 'teams_transcripts_imported', value: 1 })
      queryClient.invalidateQueries({ queryKey: ['recordings', projectId] })
      setShowTeamsModal(false)
      setSelectedMeeting(null)
      setMeetingTranscripts([])
    },
  })

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files
    if (!files || files.length === 0) return

    setUploading(true)
    const formData = new FormData()
    formData.append('file', files[0])
    formData.append('project_id', projectId!)
    formData.append('workshop_type', 'Workshop')

    uploadMutation.mutate(formData)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const handleTranscribe = (recording: Recording) => {
    transcribeMutation.mutate(recording.id)
  }

  const handleDelete = (recording: Recording) => {
    setDeleteConfirmRecording(recording)
  }

  const confirmDelete = () => {
    if (deleteConfirmRecording) {
      deleteMutation.mutate(deleteConfirmRecording.id)
      setDeleteConfirmRecording(null)
    }
  }

  const handleSelectMeeting = async (meeting: TeamsMeeting) => {
    setSelectedMeeting(meeting)
    setLoadingTranscripts(true)
    try {
      const response = await getTeamsTranscripts(meeting.id, meeting.join_url)
      setMeetingTranscripts(response.data)
      // Update transcript availability
      setTranscriptAvailability((prev) => ({
        ...prev,
        [meeting.id]: response.data.length > 0
      }))
    } catch (error) {
      console.error('Failed to fetch transcripts:', error)
      setMeetingTranscripts([])
      setTranscriptAvailability((prev) => ({
        ...prev,
        [meeting.id]: false
      }))
    } finally {
      setLoadingTranscripts(false)
    }
  }

  // Check transcript availability for a meeting (background check)
  const checkTranscriptAvailability = async (meeting: TeamsMeeting) => {
    if (transcriptAvailability[meeting.id] !== undefined) return // Already checked

    setTranscriptAvailability((prev) => ({ ...prev, [meeting.id]: 'loading' }))
    try {
      const response = await getTeamsTranscripts(meeting.id, meeting.join_url)
      setTranscriptAvailability((prev) => ({
        ...prev,
        [meeting.id]: response.data.length > 0
      }))
    } catch {
      setTranscriptAvailability((prev) => ({
        ...prev,
        [meeting.id]: false
      }))
    }
  }

  // Check transcript availability for visible meetings when modal opens
  useMemo(() => {
    if (showTeamsModal && teamsMeetings) {
      // Check first 10 meetings for transcript availability
      teamsMeetings.slice(0, 10).forEach((meeting) => {
        if (transcriptAvailability[meeting.id] === undefined) {
          checkTranscriptAvailability(meeting)
        }
      })
    }
  }, [showTeamsModal, teamsMeetings])

  const handleImportTranscript = (transcript: TeamsTranscript) => {
    if (!selectedMeeting || !projectId) return
    importMutation.mutate({
      meeting_id: transcript.meeting_id,
      transcript_id: transcript.id,
      project_id: projectId,
      meeting_subject: selectedMeeting.subject,
    })
  }

  const handleViewTranscript = async (recording: Recording) => {
    try {
      const response = await getTranscript(recording.id)
      setViewingTranscript(response.data)
      setTranscriptRecording(recording)
      setShowTranscriptModal(true)
    } catch (error) {
      console.error('Failed to load transcript:', error)
    }
  }

  const handleDownloadTranscript = async (recording: Recording) => {
    try {
      await downloadTranscript(recording.id, `${recording.original_filename || 'transcript'}.txt`)
    } catch (error) {
      console.error('Failed to download transcript:', error)
    }
  }

  // Compute stats
  const stats = useMemo(() => {
    if (!recordings) return { total: 0, transcribed: 0, processing: 0, totalDuration: 0 }
    return {
      total: recordings.length,
      transcribed: recordings.filter((r) => r.status === 'transcribed' || r.status === 'ready').length,
      processing: recordings.filter((r) => r.status === 'transcribing' || r.status === 'processing').length,
      totalDuration: recordings.reduce((sum, r) => sum + (r.duration_seconds || 0), 0),
    }
  }, [recordings])

  const filteredRecordings = useMemo(() => {
    if (!recordings) return []
    let filtered = [...recordings]

    if (statusFilter !== 'all') {
      filtered = filtered.filter((r) => r.status === statusFilter)
    }

    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.original_filename.localeCompare(b.original_filename)
        case 'size':
          return b.file_size - a.file_size
        case 'duration':
          return (b.duration_seconds || 0) - (a.duration_seconds || 0)
        default:
          return new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      }
    })

    return filtered
  }, [recordings, statusFilter, sortBy])

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
  }

  const formatDuration = (seconds?: number) => {
    if (!seconds) return '--:--'
    const hours = Math.floor(seconds / 3600)
    const mins = Math.floor((seconds % 3600) / 60)
    const secs = Math.floor(seconds % 60)
    if (hours > 0) {
      return `${hours}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
    }
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }

  const formatTotalDuration = (seconds: number) => {
    const hours = (seconds / 3600).toFixed(1)
    return `${hours}h`
  }

  const getStatusBadge = (status: Recording['status']) => {
    switch (status) {
      case 'transcribed':
      case 'ready':
        return (
          <span className="px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{ background: 'rgba(52, 211, 153, 0.2)', color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
            <i className="fa-solid fa-check"></i>
            <span>Transcribed</span>
          </span>
        )
      case 'transcribing':
      case 'processing':
        return (
          <span className="px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{ background: 'rgba(251, 191, 36, 0.2)', color: '#fbbf24', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
            <i className="fa-solid fa-spinner fa-spin"></i>
            <span>Transcribing</span>
          </span>
        )
      case 'error':
        return (
          <span className="px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{ background: 'rgba(248, 113, 113, 0.2)', color: '#f87171', border: '1px solid rgba(248, 113, 113, 0.3)' }}>
            <i className="fa-solid fa-times"></i>
            <span>Error</span>
          </span>
        )
      default:
        return (
          <span className="px-3 py-1 text-xs font-medium rounded-full flex items-center space-x-1"
            style={{ background: 'rgba(148, 163, 184, 0.2)', color: 'rgba(148, 163, 184, 0.8)', border: '1px solid rgba(148, 163, 184, 0.3)' }}>
            <i className="fa-solid fa-circle text-[8px]"></i>
            <span>Uploaded</span>
          </span>
        )
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <Link
          to={`/projects/${projectId}`}
          className="text-sm font-medium flex items-center space-x-2 mb-2 transition hover:opacity-80"
          style={{ color: '#60a5fa' }}
        >
          <i className="fa-solid fa-arrow-left text-xs"></i>
          <span>Back to {project?.project_name || 'Project'}</span>
        </Link>
        <h1 className="text-2xl font-bold" style={{ color: '#e2e8f0' }}>Recordings</h1>
        <div className="flex items-center space-x-2 mt-1 text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
          <Link to="/projects" className="hover:text-blue-400 transition">
            Projects
          </Link>
          <i className="fa-solid fa-chevron-right text-xs"></i>
          <Link to={`/projects/${projectId}`} className="hover:text-blue-400 transition">
            {project?.project_name || 'Project'}
          </Link>
          <i className="fa-solid fa-chevron-right text-xs"></i>
          <span style={{ color: '#e2e8f0' }}>Recordings</span>
        </div>
        <p className="text-sm mt-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Upload and transcribe workshop recordings</p>
      </div>

      {/* Upload Options */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Teams Import Card - Primary Option */}
        <div
          className="rounded-xl cursor-pointer transition-all duration-300 hover:scale-[1.02]"
          style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)', border: '2px solid rgba(59, 130, 246, 0.4)' }}
          onClick={() => setShowTeamsModal(true)}
        >
          <div className="p-12 text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4) 0%, rgba(139, 92, 246, 0.4) 100%)', border: '1px solid rgba(100, 180, 255, 0.3)', boxShadow: '0 0 30px rgba(59, 130, 246, 0.3)' }}>
              <svg className="w-10 h-10" viewBox="0 0 24 24" fill="#60a5fa">
                <path d="M19.2 6.4h-4.8v4.8h4.8V6.4zm-14.4 0v4.8h4.8V6.4H4.8zm9.6 9.6v4.8h4.8V16h-4.8zM4.8 16v4.8h4.8V16H4.8zm4.8-4.8H4.8v4.8h4.8v-4.8zm4.8 0h-4.8v4.8h4.8v-4.8zm4.8 0h-4.8v4.8h4.8v-4.8zM9.6 6.4H4.8v4.8h4.8V6.4zm4.8 0H9.6v4.8h4.8V6.4z"/>
              </svg>
            </div>
            <div className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium mb-4"
              style={{ background: 'rgba(59, 130, 246, 0.2)', color: '#60a5fa', border: '1px solid rgba(59, 130, 246, 0.3)' }}>
              <i className="fa-solid fa-star mr-1"></i> Recommended
            </div>
            <h3 className="text-xl font-bold mb-2" style={{ color: '#e2e8f0' }}>Import from Teams</h3>
            <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
              Import transcripts directly from your Teams meetings
            </p>
            <button className="px-6 py-3 rounded-lg font-medium transition"
              style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.4) 0%, rgba(139, 92, 246, 0.4) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}>
              Browse Meetings
            </button>
            <p className="text-xs mt-4" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Access meetings you've attended or organized</p>
          </div>
        </div>

        {/* Upload Recording Card */}
        <div
          className="rounded-xl cursor-pointer transition-all duration-300 hover:scale-[1.02]"
          style={{ ...glassPanel, borderStyle: 'dashed', borderWidth: '2px' }}
          onClick={() => fileInputRef.current?.click()}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept=".mp3,.mp4,.wav,.m4a,.webm,.ogg"
            onChange={handleFileSelect}
            className="hidden"
          />
          <div className="p-12 text-center">
            {uploading ? (
              <>
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{ background: 'rgba(96, 165, 250, 0.15)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
                  <i className="fa-solid fa-spinner fa-spin text-3xl" style={{ color: '#60a5fa' }}></i>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#e2e8f0' }}>Uploading...</h3>
                <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Please wait while your file is being uploaded</p>
              </>
            ) : (
              <>
                <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
                  style={{ background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
                  <i className="fa-solid fa-upload text-3xl" style={{ color: '#34d399' }}></i>
                </div>
                <h3 className="text-xl font-bold mb-2" style={{ color: '#e2e8f0' }}>Upload Recording</h3>
                <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Drag and drop or click to select files</p>
                <div className="flex flex-wrap justify-center gap-2 mb-6">
                  {['.mp3', '.mp4', '.wav', '.m4a', '.webm', '.ogg'].map((ext) => (
                    <span key={ext} className="px-3 py-1 rounded-full text-sm"
                      style={{ background: 'rgba(100, 116, 139, 0.3)', color: 'rgba(148, 163, 184, 0.8)' }}>{ext}</span>
                  ))}
                </div>
                <button className="px-6 py-3 rounded-lg font-medium transition"
                  style={{ background: 'rgba(52, 211, 153, 0.2)', border: '1px solid rgba(52, 211, 153, 0.4)', color: '#34d399' }}>
                  Select Files
                </button>
                <p className="text-xs mt-4" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>Maximum file size: 2GB per file</p>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="rounded-xl p-6" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(167, 139, 250, 0.15)', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
              <i className="fa-solid fa-microphone text-xl" style={{ color: '#a78bfa' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Total Recordings</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.total}</p>
        </div>

        <div className="rounded-xl p-6" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
              <i className="fa-solid fa-check-circle text-xl" style={{ color: '#34d399' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Transcribed</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.transcribed}</p>
        </div>

        <div className="rounded-xl p-6" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(251, 191, 36, 0.15)', border: '1px solid rgba(251, 191, 36, 0.3)' }}>
              <i className="fa-solid fa-spinner text-xl" style={{ color: '#fbbf24' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Processing</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{stats.processing}</p>
        </div>

        <div className="rounded-xl p-6" style={glassPanel}>
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center"
              style={{ background: 'rgba(96, 165, 250, 0.15)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
              <i className="fa-solid fa-clock text-xl" style={{ color: '#60a5fa' }}></i>
            </div>
          </div>
          <h4 className="text-sm font-medium mb-1" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>Total Duration</h4>
          <p className="text-3xl font-bold" style={{ color: '#e2e8f0' }}>{formatTotalDuration(stats.totalDuration)}</p>
        </div>
      </div>

      {/* Recordings List */}
      {isLoading ? (
        <div className="flex items-center justify-center h-32">
          <div className="text-center">
            <i className="fa-solid fa-spinner fa-spin text-4xl mb-4" style={{ color: '#60a5fa' }}></i>
            <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading recordings...</p>
          </div>
        </div>
      ) : recordings && recordings.length > 0 ? (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold" style={{ color: '#e2e8f0' }}>All Recordings</h3>
            <div className="flex items-center space-x-3">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
              >
                <option value="all">All Status</option>
                <option value="uploaded">Uploaded</option>
                <option value="transcribing">Transcribing</option>
                <option value="transcribed">Transcribed</option>
                <option value="error">Error</option>
              </select>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
                className="text-sm rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                style={{ background: 'rgba(30, 41, 59, 0.8)', border: '1px solid rgba(100, 180, 255, 0.2)', color: '#e2e8f0' }}
              >
                <option value="date">Sort by Date</option>
                <option value="name">Sort by Name</option>
                <option value="size">Sort by Size</option>
                <option value="duration">Sort by Duration</option>
              </select>
            </div>
          </div>

          <div className="space-y-4">
            {filteredRecordings.map((recording) => (
              <div
                key={recording.id}
                className="rounded-xl p-6 transition-all duration-300 hover:scale-[1.01]"
                style={{ ...glassPanel, borderColor: recording.status === 'error' ? 'rgba(248, 113, 113, 0.3)' : 'rgba(100, 180, 255, 0.1)' }}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4 flex-1">
                    <div
                      className="w-12 h-12 rounded-lg flex items-center justify-center"
                      style={{
                        background: recording.status === 'error' ? 'rgba(248, 113, 113, 0.15)' : 'rgba(167, 139, 250, 0.15)',
                        border: recording.status === 'error' ? '1px solid rgba(248, 113, 113, 0.3)' : '1px solid rgba(167, 139, 250, 0.3)'
                      }}
                    >
                      <i
                        className="fa-solid fa-microphone text-xl"
                        style={{ color: recording.status === 'error' ? '#f87171' : '#a78bfa' }}
                      ></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-base font-semibold mb-1" style={{ color: '#e2e8f0' }}>
                        {recording.original_filename}
                      </h4>
                      <div className="flex items-center space-x-4 text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                        <span className="flex items-center space-x-1">
                          <i className="fa-solid fa-database text-xs"></i>
                          <span>{formatFileSize(recording.file_size)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <i className="fa-solid fa-clock text-xs"></i>
                          <span>{formatDuration(recording.duration_seconds)}</span>
                        </span>
                        <span className="flex items-center space-x-1">
                          <i className="fa-solid fa-calendar text-xs"></i>
                          <span>{new Date(recording.created_at).toLocaleDateString()}</span>
                        </span>
                      </div>
                      {recording.error_message && (
                        <p className="text-sm flex items-center space-x-1 mt-2" style={{ color: '#f87171' }}>
                          <i className="fa-solid fa-exclamation-triangle"></i>
                          <span>{recording.error_message}</span>
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center space-x-4">
                    {getStatusBadge(recording.status)}
                    {(recording.status === 'transcribed' || recording.status === 'ready') && (
                      <>
                        <button
                          onClick={() => handleViewTranscript(recording)}
                          title="View transcript"
                          className="p-2 rounded-lg transition"
                          style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                          onMouseOver={(e) => { e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)' }}
                          onMouseOut={(e) => { e.currentTarget.style.color = 'rgba(148, 163, 184, 0.6)'; e.currentTarget.style.background = 'transparent' }}
                        >
                          <i className="fa-solid fa-eye"></i>
                        </button>
                        <button
                          onClick={() => handleDownloadTranscript(recording)}
                          title="Download transcript"
                          className="p-2 rounded-lg transition"
                          style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                          onMouseOver={(e) => { e.currentTarget.style.color = '#60a5fa'; e.currentTarget.style.background = 'rgba(96, 165, 250, 0.1)' }}
                          onMouseOut={(e) => { e.currentTarget.style.color = 'rgba(148, 163, 184, 0.6)'; e.currentTarget.style.background = 'transparent' }}
                        >
                          <i className="fa-solid fa-download"></i>
                        </button>
                      </>
                    )}
                    {(recording.status === 'uploaded' || recording.status === 'error') && (
                      <button
                        onClick={() => handleTranscribe(recording)}
                        disabled={transcribeMutation.isPending}
                        className="px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-70"
                        style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
                      >
                        {recording.status === 'error' ? 'Retry' : 'Transcribe'}
                      </button>
                    )}
                    <button
                      onClick={() => handleDelete(recording)}
                      className="p-2 rounded-lg transition"
                      style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                      onMouseOver={(e) => { e.currentTarget.style.color = '#f87171'; e.currentTarget.style.background = 'rgba(248, 113, 113, 0.1)' }}
                      onMouseOut={(e) => { e.currentTarget.style.color = 'rgba(148, 163, 184, 0.6)'; e.currentTarget.style.background = 'transparent' }}
                    >
                      <i className="fa-solid fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="rounded-xl p-12 text-center" style={glassPanel}>
          <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
            style={{ background: 'rgba(100, 116, 139, 0.2)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
            <i className="fa-solid fa-microphone text-3xl" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
          </div>
          <h3 className="text-lg font-semibold mb-2" style={{ color: '#e2e8f0' }}>No recordings yet</h3>
          <p className="mb-6" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
            Upload your first workshop recording to get started
          </p>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="px-6 py-3 rounded-lg text-sm font-medium transition inline-flex items-center space-x-2"
            style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
          >
            <i className="fa-solid fa-upload"></i>
            <span>Upload Recording</span>
          </button>
        </div>
      )}

      {/* Tips Section */}
      {recordings && recordings.length > 0 && (
        <div className="rounded-xl p-8"
          style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.15) 0%, rgba(139, 92, 246, 0.15) 100%)', border: '1px solid rgba(100, 180, 255, 0.2)' }}>
          <div className="flex items-start space-x-4">
            <div className="w-12 h-12 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(100, 180, 255, 0.3)', boxShadow: '0 0 20px rgba(59, 130, 246, 0.2)' }}>
              <i className="fa-solid fa-lightbulb text-xl" style={{ color: '#fbbf24' }}></i>
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-bold mb-3" style={{ color: '#e2e8f0' }}>Tips for Better Transcription</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex items-start space-x-3">
                  <i className="fa-solid fa-check-circle mt-1" style={{ color: '#34d399' }}></i>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#e2e8f0' }}>Clear Audio Quality</p>
                    <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                      Use a good microphone and minimize background noise
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="fa-solid fa-check-circle mt-1" style={{ color: '#34d399' }}></i>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#e2e8f0' }}>Proper File Format</p>
                    <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>MP3, MP4, and WAV formats work best</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="fa-solid fa-check-circle mt-1" style={{ color: '#34d399' }}></i>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#e2e8f0' }}>Speaker Separation</p>
                    <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                      Clear speech helps identify multiple speakers
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <i className="fa-solid fa-check-circle mt-1" style={{ color: '#34d399' }}></i>
                  <div>
                    <p className="text-sm font-medium" style={{ color: '#e2e8f0' }}>File Size Limit</p>
                    <p className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                      Keep files under 2GB for optimal processing
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Teams Import Modal */}
      {showTeamsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" style={{ backdropFilter: 'blur(4px)' }}>
          <div className="rounded-xl w-full max-w-2xl max-h-[80vh] overflow-hidden"
            style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(100, 180, 255, 0.2)' }}>
            <div className="p-6" style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: 'rgba(96, 165, 250, 0.15)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
                    <i className="fa-brands fa-microsoft" style={{ color: '#60a5fa' }}></i>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold" style={{ color: '#e2e8f0' }}>
                      {selectedMeeting ? 'Select Transcript' : 'Import from Teams'}
                    </h2>
                    <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                      {selectedMeeting
                        ? `Transcripts for "${selectedMeeting.subject}"`
                        : 'Select a meeting to import its transcript'}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    setShowTeamsModal(false)
                    setSelectedMeeting(null)
                    setMeetingTranscripts([])
                    setTeamsSearch('')
                  }}
                  className="p-2 rounded-lg transition"
                  style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                  onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(100, 116, 139, 0.3)' }}
                  onMouseOut={(e) => { e.currentTarget.style.background = 'transparent' }}
                >
                  <i className="fa-solid fa-times"></i>
                </button>
              </div>

              {/* Date range and search filters - only show in meetings list view */}
              {!selectedMeeting && (
                <div className="space-y-3">
                  {/* Date range chips */}
                  <div className="flex items-center space-x-2">
                    <span className="text-xs font-medium" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>Date range:</span>
                    {(['30d', '60d', '90d'] as const).map((range) => (
                      <button
                        key={range}
                        onClick={() => setTeamsDateRange(range)}
                        className="px-3 py-1.5 rounded-lg text-xs font-medium transition"
                        style={{
                          background: teamsDateRange === range
                            ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)'
                            : 'rgba(30, 41, 59, 0.5)',
                          border: teamsDateRange === range
                            ? '1px solid rgba(96, 165, 250, 0.4)'
                            : '1px solid rgba(100, 180, 255, 0.1)',
                          color: teamsDateRange === range ? '#e2e8f0' : 'rgba(148, 163, 184, 0.8)'
                        }}
                      >
                        {range === '30d' ? 'Last 30 days' : range === '60d' ? 'Last 60 days' : 'Last 90 days'}
                      </button>
                    ))}
                  </div>

                  {/* Search and filter row */}
                  <div className="flex items-center space-x-3">
                    <div className="relative flex-1">
                      <input
                        type="text"
                        placeholder="Search meetings by subject or organizer..."
                        value={teamsSearch}
                        onChange={(e) => setTeamsSearch(e.target.value)}
                        className="w-full pl-9 pr-4 py-2 rounded-lg text-sm"
                        style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.15)', color: '#e2e8f0' }}
                      />
                      <i className="fa-solid fa-search absolute left-3 top-2.5 text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
                    </div>
                    <label className="flex items-center space-x-2 cursor-pointer whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={showOnlyWithTranscripts}
                        onChange={(e) => setShowOnlyWithTranscripts(e.target.checked)}
                        className="rounded"
                        style={{ accentColor: '#60a5fa' }}
                      />
                      <span className="text-xs" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>With transcripts only</span>
                    </label>
                  </div>
                </div>
              )}
            </div>

            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {selectedMeeting ? (
                // Transcript selection view
                <div>
                  <button
                    onClick={() => {
                      setSelectedMeeting(null)
                      setMeetingTranscripts([])
                    }}
                    className="text-sm flex items-center space-x-1 mb-4 transition hover:opacity-80"
                    style={{ color: '#60a5fa' }}
                  >
                    <i className="fa-solid fa-arrow-left"></i>
                    <span>Back to meetings</span>
                  </button>

                  {loadingTranscripts ? (
                    <div className="text-center py-8">
                      <i className="fa-solid fa-spinner fa-spin text-2xl mb-2" style={{ color: '#60a5fa' }}></i>
                      <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading transcripts...</p>
                    </div>
                  ) : meetingTranscripts.length > 0 ? (
                    <div className="space-y-3">
                      {meetingTranscripts.map((transcript) => (
                        <div
                          key={transcript.id}
                          className="flex items-center justify-between p-4 rounded-lg"
                          style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}
                        >
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                              style={{ background: 'rgba(52, 211, 153, 0.15)', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
                              <i className="fa-solid fa-file-lines" style={{ color: '#34d399' }}></i>
                            </div>
                            <div>
                              <p className="font-medium" style={{ color: '#e2e8f0' }}>Meeting Transcript</p>
                              <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                                {transcript.created_at
                                  ? new Date(transcript.created_at).toLocaleString()
                                  : 'Date unknown'}
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => handleImportTranscript(transcript)}
                            disabled={importMutation.isPending}
                            className="px-4 py-2 rounded-lg text-sm font-medium transition disabled:opacity-70"
                            style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
                          >
                            {importMutation.isPending ? (
                              <>
                                <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                                Importing...
                              </>
                            ) : (
                              'Import'
                            )}
                          </button>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                        style={{ background: 'rgba(100, 116, 139, 0.2)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                        <i className="fa-solid fa-file-circle-xmark text-2xl" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
                      </div>
                      <h3 className="font-semibold mb-2" style={{ color: '#e2e8f0' }}>No transcripts available</h3>
                      <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                        This meeting doesn't have any transcripts yet.
                        <br />
                        Transcripts are generated when recording is enabled.
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                // Meetings list view
                <div>
                  {loadingMeetings ? (
                    <div className="text-center py-8">
                      <i className="fa-solid fa-spinner fa-spin text-2xl mb-2" style={{ color: '#60a5fa' }}></i>
                      <p style={{ color: 'rgba(148, 163, 184, 0.8)' }}>Loading your Teams meetings...</p>
                    </div>
                  ) : filteredTeamsMeetings.length > 0 ? (
                    <div className="space-y-3">
                      {/* Show count of filtered results */}
                      <div className="text-xs mb-2" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                        Showing {filteredTeamsMeetings.length} of {teamsMeetings?.length || 0} meetings
                      </div>
                      {filteredTeamsMeetings.map((meeting) => {
                        const availability = transcriptAvailability[meeting.id]
                        return (
                          <div
                            key={meeting.id}
                            onClick={() => handleSelectMeeting(meeting)}
                            className="flex items-center justify-between p-4 rounded-lg cursor-pointer transition-all duration-300"
                            style={{ background: 'rgba(30, 41, 59, 0.5)', border: '1px solid rgba(100, 180, 255, 0.1)' }}
                            onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(30, 41, 59, 0.8)'; e.currentTarget.style.borderColor = 'rgba(96, 165, 250, 0.3)' }}
                            onMouseOut={(e) => { e.currentTarget.style.background = 'rgba(30, 41, 59, 0.5)'; e.currentTarget.style.borderColor = 'rgba(100, 180, 255, 0.1)' }}
                          >
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                              <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
                                style={{ background: 'rgba(96, 165, 250, 0.15)', border: '1px solid rgba(96, 165, 250, 0.3)' }}>
                                <i className="fa-solid fa-video" style={{ color: '#60a5fa' }}></i>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium truncate" style={{ color: '#e2e8f0' }}>{meeting.subject}</p>
                                <div className="flex items-center space-x-3 text-sm flex-wrap" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                                  {meeting.start && (
                                    <span>
                                      <i className="fa-solid fa-calendar mr-1"></i>
                                      {new Date(meeting.start).toLocaleDateString()}
                                    </span>
                                  )}
                                  {meeting.organizer && (
                                    <span className="truncate max-w-[150px]">
                                      <i className="fa-solid fa-user mr-1"></i>
                                      {meeting.organizer}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                            <div className="flex items-center space-x-2 flex-shrink-0 ml-2">
                              {/* Transcript availability indicator */}
                              {availability === 'loading' ? (
                                <i className="fa-solid fa-spinner fa-spin text-xs" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
                              ) : availability === true ? (
                                <span className="px-2 py-0.5 rounded text-xs font-medium"
                                  style={{ background: 'rgba(52, 211, 153, 0.15)', color: '#34d399', border: '1px solid rgba(52, 211, 153, 0.3)' }}>
                                  <i className="fa-solid fa-check mr-1"></i>Has transcript
                                </span>
                              ) : availability === false ? (
                                <span className="px-2 py-0.5 rounded text-xs font-medium"
                                  style={{ background: 'rgba(148, 163, 184, 0.1)', color: 'rgba(148, 163, 184, 0.5)', border: '1px solid rgba(148, 163, 184, 0.2)' }}>
                                  No transcript
                                </span>
                              ) : null}
                              <i className="fa-solid fa-chevron-right" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                  ) : teamsMeetings && teamsMeetings.length > 0 ? (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                        style={{ background: 'rgba(100, 116, 139, 0.2)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                        <i className="fa-solid fa-filter text-2xl" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
                      </div>
                      <h3 className="font-semibold mb-2" style={{ color: '#e2e8f0' }}>No matching meetings</h3>
                      <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                        Try adjusting your search or filter criteria.
                      </p>
                      <button
                        onClick={() => { setTeamsSearch(''); setShowOnlyWithTranscripts(false) }}
                        className="mt-4 px-4 py-2 rounded-lg text-sm font-medium transition"
                        style={{ background: 'rgba(96, 165, 250, 0.15)', border: '1px solid rgba(96, 165, 250, 0.3)', color: '#60a5fa' }}
                      >
                        Clear filters
                      </button>
                    </div>
                  ) : (
                    <div className="text-center py-8">
                      <div className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4"
                        style={{ background: 'rgba(100, 116, 139, 0.2)', border: '1px solid rgba(100, 180, 255, 0.1)' }}>
                        <i className="fa-solid fa-video-slash text-2xl" style={{ color: 'rgba(148, 163, 184, 0.5)' }}></i>
                      </div>
                      <h3 className="font-semibold mb-2" style={{ color: '#e2e8f0' }}>No meetings found</h3>
                      <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>
                        No Teams meetings found in the selected date range.
                        <br />
                        Try selecting a longer date range.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmRecording && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" style={{ backdropFilter: 'blur(4px)' }}>
          <div className="rounded-xl w-full max-w-md p-6" style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(248, 113, 113, 0.3)' }}>
            <div className="text-center">
              <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
                style={{ background: 'rgba(248, 113, 113, 0.15)', border: '1px solid rgba(248, 113, 113, 0.3)' }}>
                <i className="fa-solid fa-trash text-2xl" style={{ color: '#f87171' }}></i>
              </div>
              <h3 className="text-xl font-bold mb-2" style={{ color: '#e2e8f0' }}>Whoa there, buddy! 🤔</h3>
              <p className="text-sm mb-2" style={{ color: 'rgba(148, 163, 184, 0.8)' }}>
                You're about to delete this recording:
              </p>
              <p className="font-medium mb-4 px-4 py-2 rounded-lg" style={{ color: '#f87171', background: 'rgba(248, 113, 113, 0.1)' }}>
                "{deleteConfirmRecording.original_filename}"
              </p>
              <p className="text-sm mb-6" style={{ color: 'rgba(148, 163, 184, 0.6)' }}>
                This action is permanent and cannot be undone. The transcript will also be deleted. Are you absolutely sure?
              </p>
              <div className="flex items-center justify-center space-x-3">
                <button
                  onClick={() => setDeleteConfirmRecording(null)}
                  className="px-5 py-2.5 rounded-lg text-sm font-medium transition"
                  style={{ border: '1px solid rgba(100, 180, 255, 0.2)', color: 'rgba(148, 163, 184, 0.8)' }}
                >
                  Nah, keep it
                </button>
                <button
                  onClick={confirmDelete}
                  disabled={deleteMutation.isPending}
                  className="px-5 py-2.5 rounded-lg text-sm font-medium transition flex items-center space-x-2"
                  style={{ background: 'rgba(248, 113, 113, 0.3)', border: '1px solid rgba(248, 113, 113, 0.4)', color: '#f87171' }}
                >
                  {deleteMutation.isPending ? (
                    <><i className="fa-solid fa-spinner fa-spin"></i><span>Deleting...</span></>
                  ) : (
                    <><i className="fa-solid fa-trash"></i><span>Yes, delete it</span></>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Transcript View Modal */}
      {showTranscriptModal && viewingTranscript && transcriptRecording && (
        <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50" style={{ backdropFilter: 'blur(4px)' }}>
          <div className="rounded-xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
            style={{ background: 'rgba(15, 23, 42, 0.95)', border: '1px solid rgba(100, 180, 255, 0.2)' }}>
            <div className="p-6" style={{ borderBottom: '1px solid rgba(100, 180, 255, 0.1)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center"
                    style={{ background: 'rgba(167, 139, 250, 0.15)', border: '1px solid rgba(167, 139, 250, 0.3)' }}>
                    <i className="fa-solid fa-file-lines" style={{ color: '#a78bfa' }}></i>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold" style={{ color: '#e2e8f0' }}>Transcript</h2>
                    <p className="text-sm" style={{ color: 'rgba(148, 163, 184, 0.7)' }}>{transcriptRecording.original_filename}</p>
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleDownloadTranscript(transcriptRecording)}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition flex items-center space-x-2"
                    style={{ background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.3) 0%, rgba(139, 92, 246, 0.3) 100%)', border: '1px solid rgba(96, 165, 250, 0.4)', color: '#e2e8f0' }}
                  >
                    <i className="fa-solid fa-download"></i>
                    <span>Download</span>
                  </button>
                  <button
                    onClick={() => {
                      setShowTranscriptModal(false)
                      setViewingTranscript(null)
                      setTranscriptRecording(null)
                    }}
                    className="p-2 rounded-lg transition"
                    style={{ color: 'rgba(148, 163, 184, 0.6)' }}
                    onMouseOver={(e) => { e.currentTarget.style.background = 'rgba(100, 116, 139, 0.3)' }}
                    onMouseOut={(e) => { e.currentTarget.style.background = 'transparent' }}
                  >
                    <i className="fa-solid fa-times"></i>
                  </button>
                </div>
              </div>
            </div>

            <div className="p-6 overflow-y-auto max-h-[70vh]">
              <div className="space-y-4">
                {viewingTranscript.sections.map((section, index) => (
                  <div key={index} className="flex space-x-4">
                    <div className="flex-shrink-0 w-16 text-sm font-mono" style={{ color: 'rgba(148, 163, 184, 0.5)' }}>
                      {formatDuration(section.start_time)}
                    </div>
                    <div className="flex-1">
                      {section.speaker && (
                        <div className="text-sm font-medium mb-1" style={{ color: '#60a5fa' }}>
                          {section.speaker}
                        </div>
                      )}
                      <p style={{ color: 'rgba(148, 163, 184, 0.9)' }}>{section.text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
